
import { useState } from 'react';

interface AircraftImage {
  url: string;
  alt: string;
}

interface Aircraft {
  id: number;
  name: string;
  tailNumber: string;
  specs: string[];
  images: AircraftImage[];
}

interface AircraftCardProps {
  aircraft: Aircraft;
}

export default function AircraftCard({ aircraft }: AircraftCardProps) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const openLightbox = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
        {/* Aircraft Header */}
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {aircraft.name}
          </h3>
          <p className="text-lg text-blue-600 font-semibold mb-4">
            {aircraft.tailNumber}
          </p>
          
          {/* Specifications */}
          <div className="space-y-2">
            {aircraft.specs.map((spec, index) => (
              <div key={index} className="flex items-center text-gray-700">
                <i className="ri-check-line text-green-500 mr-3 w-4 h-4 flex items-center justify-center"></i>
                <span>{spec}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Images */}
        <div className="p-6">
          <div className="space-y-4">
            {aircraft.images.map((image, index) => (
              <div key={index} className="relative group">
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-64 object-cover object-top rounded-lg cursor-pointer transition-transform duration-300 group-hover:scale-105"
                  onClick={() => openLightbox(image.url)}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                  <i className="ri-zoom-in-line text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-8 h-8 flex items-center justify-center"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-6xl max-h-full">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 transition-colors z-10 w-10 h-10 flex items-center justify-center"
            >
              <i className="ri-close-line w-8 h-8 flex items-center justify-center"></i>
            </button>
            <img
              src={selectedImage}
              alt="Aircraft detail"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
}
